import React from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, TextField, Grid } from '@mui/material';
import { CRANE_FORKLIFT_ITEMS } from '../constants';

const toggleSx = {
    flexShrink: 0,
    '& .MuiToggleButton-root': {
        px: 2, py: 0.5, fontSize: '0.75rem', textTransform: 'none',
        border: '1px solid #bdbdbd', minHeight: 32,
        '&.Mui-selected': { fontWeight: 600, border: '1px solid', '&:hover': { opacity: 0.9 } },
        '&:not(.Mui-selected)': { color: '#666' },
    },
};

export default function CraneForkliftForm({ results, onChange, isReadOnly }) {
    const handleItemChange = (index, value) => {
        if (value !== null) {
            const key = `item${index + 1}`;
            onChange({ ...results, [key]: value });
        }
    };

    const half = Math.ceil(CRANE_FORKLIFT_ITEMS.length / 2);
    const col1 = CRANE_FORKLIFT_ITEMS.slice(0, half);
    const col2 = CRANE_FORKLIFT_ITEMS.slice(half);

    const renderItem = (item, globalIdx) => {
        const key = `item${globalIdx + 1}`;
        const val = results[key] || '';
        return (
            <Box key={globalIdx} sx={{
                p: 0.65, border: '1px solid', borderColor: val === 'no' ? '#ffcdd2' : '#e0e0e0',
                backgroundColor: val === 'no' ? '#fff5f5' : '#fafafa',
                display: 'flex', alignItems: 'center', gap: 0.65, mb: 0.5, borderRadius: 0.5,
            }}>
                <Typography variant="body2" sx={{ fontSize: '0.725rem', lineHeight: 1.3, flex: 1, minWidth: 0 }}>
                    <strong style={{ color: '#888' }}>{globalIdx + 1}.</strong> {item}
                </Typography>
                {!isReadOnly ? (
                    <ToggleButtonGroup value={val} exclusive onChange={(_, v) => handleItemChange(globalIdx, v)}
                        size="small" sx={toggleSx}>
                        <ToggleButton value="yes" sx={{ '&.Mui-selected': { backgroundColor: '#4caf50', color: '#fff', borderColor: '#4caf50 !important', '&:hover': { backgroundColor: '#45a049' } } }}>Y</ToggleButton>
                        <ToggleButton value="no" sx={{ '&.Mui-selected': { backgroundColor: '#f44336', color: '#fff', borderColor: '#f44336 !important', '&:hover': { backgroundColor: '#e53935' } } }}>N</ToggleButton>
                        <ToggleButton value="na" sx={{ '&.Mui-selected': { backgroundColor: '#9e9e9e', color: '#fff', borderColor: '#9e9e9e !important' } }}>NA</ToggleButton>
                    </ToggleButtonGroup>
                ) : (
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                        color: val === 'yes' ? '#4caf50' : val === 'no' ? '#f44336' : '#999' }}>
                        {val ? val.toUpperCase() : '—'}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f57f17', mb: 0.5, fontSize: '0.9rem' }}>
                D-27 CONTRACTOR'S CRANE INSPECTION — INITIAL USE CHECKLIST
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Crane inspections are required when equipment arrives on site, if the crane changes possession, or is reconfigured.
            </Typography>

            <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                    <TextField label="Crane Manufacturer" size="small" fullWidth disabled={isReadOnly}
                        value={results.craneManufacturer || ''} onChange={e => onChange({ ...results, craneManufacturer: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                    <TextField label="Model" size="small" fullWidth disabled={isReadOnly}
                        value={results.model || ''} onChange={e => onChange({ ...results, model: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                    <TextField label="Serial Number" size="small" fullWidth disabled={isReadOnly}
                        value={results.serialNumber || ''} onChange={e => onChange({ ...results, serialNumber: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                    <TextField label="Contractor" size="small" fullWidth disabled={isReadOnly}
                        value={results.contractor || ''} onChange={e => onChange({ ...results, contractor: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                    <TextField label="Crane Owner" size="small" fullWidth disabled={isReadOnly}
                        value={results.craneOwner || ''} onChange={e => onChange({ ...results, craneOwner: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                    <TextField label="Date of Last Inspection" type="date" size="small" fullWidth disabled={isReadOnly}
                        InputLabelProps={{ shrink: true }}
                        value={results.dateOfLastInspection || ''} onChange={e => onChange({ ...results, dateOfLastInspection: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                    <TextField label="Yearly Inspection Performed By" size="small" fullWidth disabled={isReadOnly}
                        value={results.yearlyInspectionPerformedBy || ''} onChange={e => onChange({ ...results, yearlyInspectionPerformedBy: e.target.value })} />
                </Grid>
            </Grid>

            <Grid container spacing={0.5}>
                <Grid item xs={6}>
                    {col1.map((item, idx) => renderItem(item, idx))}
                </Grid>
                <Grid item xs={6}>
                    {col2.map((item, idx) => renderItem(item, half + idx))}
                </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Result:</Typography>
                <ToggleButtonGroup
                    value={results.approved === true ? 'approved' : results.approved === false ? 'rejected' : ''}
                    exclusive
                    onChange={(_, v) => { if (v !== null) onChange({ ...results, approved: v === 'approved' }); }}
                    size="small"
                    disabled={isReadOnly}
                    sx={toggleSx}
                >
                    <ToggleButton value="approved" sx={{ '&.Mui-selected': { backgroundColor: '#4caf50', color: '#fff', borderColor: '#4caf50 !important' } }}>APPROVED</ToggleButton>
                    <ToggleButton value="rejected" sx={{ '&.Mui-selected': { backgroundColor: '#f44336', color: '#fff', borderColor: '#f44336 !important' } }}>REJECTED</ToggleButton>
                </ToggleButtonGroup>
            </Box>
        </Box>
    );
}
